import {useEffect, useMemo, useState} from 'react';
import AdditionalGroupConfig from './components/additional-group-config';
import Sizes from './components/sizes';

import {CartProductProps} from 'modules/cart/types';
import useAdditionalGroupsCart from 'modules/cart/util/use-additional-groups-cart';
import useCart from 'modules/cart/util/use-cart';
import ProductPrice from 'modules/products/components/product-price';
import {PriceModeEnum} from 'modules/products/types';
import useSize from 'modules/products/util/use-size';
import Row from 'shared/components/row';
import {MediumText, RegularText, SemiBoldText} from 'shared/components/text';

import Column from 'shared/components/column';
import isNumber from 'shared/util/is-number';
import Loading from 'shared/migration-components/loading';
import {errorNotification} from 'shared/services/notification';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import useProductDetails from 'modules/products/hooks/use-product-details';
import Pressable from 'shared/components/pressable';
import toBrl from 'shared/util/to-brl';
import {IoAddOutline, IoClose, IoRemoveOutline} from 'react-icons/io5';
import styled, {useTheme} from 'styled-components';
import {modals} from '@mantine/modals';
import productBanner from 'shared/assets/images/sale-banner.png';

const CartProdct = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  const {productId} = useParams<{productId: string}>();
  const {index, ...params} = state as any;
  const cartProduct = params.cartProduct
    ? (JSON.parse(params.cartProduct as string) as CartProductProps)
    : undefined;
  const {data, isLoading} = useProductDetails(productId as string);
  const {addProduct, updateProduct, cart, companyQuery} = useCart();
  const [observation, setObservation] = useState<string | undefined>(
    cartProduct?.observation,
  );
  const {colors} = useTheme();

  const {
    additionalsPrice,
    setAdditionalGroupsCart,
    additionalGroupsCart,
    getAdditionalGroupCart,
  } = useAdditionalGroupsCart(
    productId as string,
    cartProduct?.additionalGroups,
  );
  const [quantity, setQuantity] = useState(cartProduct?.quantity ?? 1);
  const {size} = useSize(cartProduct?.size);

  const remainingProducts =
    data?.remainingProducts ??
    data?.sizes?.find((size) => size.saleId === size?.saleId)
      ?.remainingProducts;

  useEffect(() => {
    return () => setAdditionalGroupsCart({});
  }, []);

  const isRemainingProductOnSale = useMemo(() => {
    const saleId = data?.saleId ?? size?.saleId;

    if (!data || data?.onePerOrder || !saleId) return false;
    if (!isNumber(remainingProducts)) return false;

    const cartSaleQuantity = (cart?.products ?? []).reduce(
      (sum, someProduct) =>
        someProduct.saleId &&
        (someProduct.saleId === data?.saleId ||
          someProduct.saleId === size?.saleId)
          ? sum + someProduct.quantity
          : sum,
      0,
    );

    return cartSaleQuantity >= (remainingProducts ?? 0);
  }, [data, cart, remainingProducts, size]);

  const isEditing = index !== undefined && cartProduct;

  const hideSaleWhileEditing =
    isEditing &&
    (size?.saleId ?? data?.saleId) &&
    !cartProduct?.saleId &&
    (data?.onePerOrder || size?.onePerOrder);

  const hideSaleWhileAdding =
    !isEditing &&
    cart?.products.some(
      (someProduct) =>
        someProduct.saleId &&
        (someProduct.saleId === data?.saleId ||
          someProduct.saleId === size?.saleId) &&
        (data?.onePerOrder || size?.onePerOrder),
    );

  const hideSale = useMemo(() => {
    if (data?.onePerOrder || size?.onePerOrder)
      return hideSaleWhileAdding || hideSaleWhileEditing;

    return isRemainingProductOnSale;
  }, [
    data,
    size,
    hideSaleWhileAdding,
    hideSaleWhileEditing,
    isRemainingProductOnSale,
  ]);

  if (isLoading) return <Loading flex />;

  const {
    name,
    description,
    image,
    priceMode,
    price,
    sizes,
    additionalGroupsConfig,
    companyId,
    salePrice,
  } = data!;

  const getPrice = () => {
    const productPrice =
      priceMode === 'fixed'
        ? !hideSale
          ? salePrice ?? price
          : price
        : (!hideSale ? size?.salePrice ?? size?.price : size?.price) ?? 0;

    return (productPrice + additionalsPrice) * quantity;
  };

  const handleProduct = () => {
    if (!companyQuery.data?.isOpened)
      return errorNotification({
        title: 'Erro ao adicionar produto ao carrinho',
        message: 'A loja está fechada no momento!',
      });

    let error = '';
    if (priceMode === PriceModeEnum.size && !size)
      return errorNotification({
        title: 'Erro ao adicionar produto',
        message: 'Nenhum tamanho selecionado',
      });

    additionalGroupsConfig.forEach((additionalGroupConfig) => {
      const additionalGroupCart = getAdditionalGroupCart(
        additionalGroupConfig.id,
      );

      const quantityOfAdditionals =
        additionalGroupCart?.reduce(
          (sum, additional) => sum + additional.quantity,
          0,
        ) ?? 0;

      if (
        additionalGroupConfig.min > 0 &&
        quantityOfAdditionals < additionalGroupConfig.min
      ) {
        error = `Selecione no mínimo ${additionalGroupConfig.min} ${additionalGroupConfig.additionalGroup.name}`;
      }
    });

    if (error)
      return errorNotification({
        title: 'Erro ao adicionar produto',
        message: error,
      });

    const product = {
      id: productId,
      quantity,
      size,
      saleId: !hideSale ? data?.saleId ?? size?.saleId : undefined,
      onePerOrder: !hideSale
        ? data?.onePerOrder ?? size?.onePerOrder
        : undefined,
      additionalGroups: additionalGroupsCart,
      observation,
    } as CartProductProps;

    const totalProductQuantityWithSale =
      cart?.products.reduce(
        (sum, someProduct) =>
          someProduct.saleId === product.saleId
            ? sum + someProduct.quantity
            : sum,
        product.quantity,
      ) ?? product.quantity;

    if (
      !hideSale &&
      !product.onePerOrder &&
      remainingProducts &&
      totalProductQuantityWithSale > remainingProducts
    )
      return modals.openConfirmModal({
        title: `Restam ${remainingProducts} item(s) em promoção`,
        children:
          'Limite máximo de itens da promoção alcançado, o excedente terá o preço normal.',
        labels: {confirm: 'Continuar', cancel: 'Voltar'},
        onConfirm: () => {
          const quantityWithoutSale =
            totalProductQuantityWithSale - remainingProducts;
          const quantityWithSale = product.quantity - quantityWithoutSale;

          if (quantityWithSale > 0)
            addProduct(companyId, {
              ...product,
              quantity: quantityWithSale,
            });
          addProduct(companyId, {
            ...product,
            quantity: quantityWithoutSale,
            onePerOrder: undefined,
            saleId: undefined,
          });
          navigate(-1);
        },
      });

    const isSaleInCart =
      product.saleId &&
      cart?.products.find(
        (someProduct) =>
          someProduct.saleId === product.saleId && product.onePerOrder,
      );

    if (!hideSale && isSaleInCart)
      return modals.openConfirmModal({
        title: 'Promoção limitada a um item',
        children:
          'Existem itens no seu carrinho utilizando a promoção atual, o produto adicionado terá o preço normal',
        labels: {confirm: 'Continuar', cancel: 'Voltar'},
        onConfirm: () => {
          addProduct(companyId, {
            ...product,
            onePerOrder: undefined,
            saleId: undefined,
          });
          navigate(-1);
        },
      });

    if (!hideSale && product.saleId && product.onePerOrder && quantity > 1)
      return modals.openConfirmModal({
        title: 'Promoção limitada a um item',
        children:
          'O item adicionado é limitado a um uso na promoção, o excedente terá o preço normal',
        labels: {confirm: 'Continuar', cancel: 'Voltar'},
        onConfirm: () => {
          addProduct(companyId, {...product, quantity: 1});
          addProduct(companyId, {
            ...product,
            quantity: product.quantity - 1,
            onePerOrder: undefined,
            saleId: undefined,
          });
          navigate(-1);
        },
      });

    if (index === undefined && cart?.companyId && cart.companyId !== companyId)
      return modals.openConfirmModal({
        title: 'Adicionar produto',
        children:
          'O carrinho já possui itens de outra loja, deseja realmente substituir?',
        labels: {confirm: 'Sim', cancel: 'Não'},
        onConfirm: () => {
          addProduct(companyId, product);
          navigate(-1);
        },
      });

    if (index !== undefined && cartProduct) updateProduct(product, +index);
    else addProduct(companyId, product);

    navigate(-1);
  };

  return (
    <Container>
      <Banner>
        <BannerImage src={image ? image : productBanner} />
      </Banner>
      <ContentColumn>
        <Row
          justifyContent="center"
          style={{
            padding: 10,
            paddingRight: 20,
            paddingLeft: 0,
          }}>
          <MediumText style={{flex: 1}} textAlign="center">
            {name}
          </MediumText>
          <Pressable onClick={() => navigate(-1)}>
            <IoClose size={28} />
          </Pressable>
        </Row>
        <ScrollArea>
          <Description>
            {description && (
              <RegularText size="small">{description}</RegularText>
            )}
            <ProductPrice {...{...data!, productIndex: +(index as string)}} />
          </Description>
          {priceMode === 'size' && (
            <Sizes {...{sizes, productIndex: +(index as string)}} />
          )}
          {additionalGroupsConfig.map((additionalGroupConfig) => (
            <AdditionalGroupConfig
              key={additionalGroupConfig.id}
              {...additionalGroupConfig}
            />
          ))}
          <Column gap={10}>
            <SemiBoldText>Observação</SemiBoldText>
            <ObservationInput
              placeholder="Alguma observação para o produto?"
              onChange={(e) => setObservation(e.target.value)}
              value={observation}
            />
          </Column>
        </ScrollArea>
      </ContentColumn>
      <Footer>
        <Quantity>
          <QuantityButton
            onClick={() => setQuantity(Math.max(quantity - 1, 1))}>
            <IoRemoveOutline color={colors.primary} />
          </QuantityButton>
          <SemiBoldText>{quantity}</SemiBoldText>
          <QuantityButton
            onClick={() =>
              isEditing && cartProduct.onePerOrder
                ? errorNotification({
                    title: 'Erro ao alterar quantidade',
                    message: 'Esse item é limitado a um uso na promoção!',
                  })
                : setQuantity(quantity + 1)
            }>
            <IoAddOutline color={colors.primary} />
          </QuantityButton>
        </Quantity>
        <Button onClick={handleProduct}>
          <Row gap={50} justifyContent="space-between">
            <SemiBoldText size="small" color="white">
              {isEditing ? 'Atualizar' : 'Adicionar'}
            </SemiBoldText>
            {
              <SemiBoldText size="small" color="white">
                {toBrl(getPrice())}
              </SemiBoldText>
            }
          </Row>
        </Button>
      </Footer>
    </Container>
  );
};

export default CartProdct;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: white;
  padding-left: 20px;
  gap: 25px;

  height: 60vh;
  padding-bottom: 60px;
  flex: auto;
  position: relative;
  overflow: hidden;
`;

const Banner = styled.div`
  display: flex;
  flex: 1;
  padding: 40px 0;
  justify-content: center;
  align-items: center;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: white;
  padding: 15px 0 15px 0;
`;

const ObservationInput = styled.textarea`
  height: 150px;
  border: 1px solid ${(props) => props.theme.colors.inputBorder};
  border-radius: 4px;
  padding: 10px;

  font-size: 16px;
  resize: none;
  outline: none;

  vertical-align: top;
`;

const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 30px;

  padding-top: 15px;
  padding-right: 20px;
  padding-left: 20px;
  padding-bottom: 15px;

  background-color: white;

  box-shadow: 0px 2px 3.84px rgba(0, 0, 0, 0.25);
`;

const Quantity = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  border: 1px solid ${(props) => props.theme.colors.softBackground};
  border-radius: 5px;
`;

const QuantityButton = styled.button`
  padding: 10px 15px;
  border: none;
  background: none;
  cursor: pointer;
`;

const ScrollArea = styled.div`
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-right: 20px;
`;

const Button = styled.button`
  width: fit-content;
  padding: 10px 15px;
  font-size: 14px;
  background-color: ${(props) => props.theme.colors.primary};
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
  color: white;
  border-radius: 3px;
  font-weight: bold;
  position: relative;
  overflow: hidden;
  gap: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryHover};
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding-bottom: 60px;
`;
